import { Component, computed, inject, input, viewChild } from '@angular/core';
import { BottomSheetComponent } from '../../../bottom-sheet/bottom-sheet.component';
import { HeaderSymbol } from '../../../header/events/header-events.model';
import { TradingTimeService } from '../../../../services/trading-time.service';
import { IconName, IconNames } from '@icon/assets';
import { UiButtonComponent, UIIconComponent, UiDividerComponent } from '@design-system';
import { MarketTimeSlicerComponent } from '../market-time-slicer/market-time-slicer.component';
import { MarketClockState } from '../../app-clock.model';
import {
	DEFAULT_MARKET_GROUP_ID,
	tradingDaysFor,
	marketGroupFor,
	marketScheduleForToday,
	nextMarketChange,
	formatMarketCountdown,
	tradingDayPeriodsFor
} from '../../utils/clock.utils';

const STATE_STYLE: Record<MarketClockState, { icon: IconNames; badgeClass: string }> = {
	trading: { icon: IconName.ClockCheck, badgeClass: 'bg-green-500/10 text-green-500' },
	preTrading: { icon: IconName.Clock, badgeClass: 'bg-yellow-500/10 text-yellow-500' },
	closed: { icon: IconName.ClockError, badgeClass: 'bg-red-500/10 text-red-500' }
};

@Component({
	selector: 'app-trading-time-bottom-sheet',
	imports: [BottomSheetComponent, MarketTimeSlicerComponent, UIIconComponent, UiButtonComponent, UiDividerComponent],
	templateUrl: './trading-time-bottom-sheet.component.html',
	host: { 'data-cy': 'trading-time-bottom-sheet' }
})
export class TradingTimeBottomSheetComponent {
	private readonly sheet = viewChild<BottomSheetComponent>('sheet');
	private readonly tradingTimeService = inject(TradingTimeService);
	protected readonly icons = IconName;

	readonly symbol = input<HeaderSymbol | null>(null);
	readonly selectedGroup = computed(() =>
		marketGroupFor(this.tradingTimeService.marketGroups(), this.symbol()?.groupId ?? DEFAULT_MARKET_GROUP_ID)
	);
	readonly selectedTitle = computed(() => this.selectedGroup()?.groupNameFa ?? '');
	readonly tradingDays = computed(() => tradingDaysFor(this.selectedGroup()));

	readonly currentTime = input(new Date());
	readonly todayMarketSchedule = computed(() => marketScheduleForToday(this.selectedGroup(), this.currentTime()));
	/** Today's periods, or the next trading day's when today has no sessions. */
	readonly tradingDay = computed(() => tradingDayPeriodsFor(this.selectedGroup(), this.currentTime()));
	readonly todayTradingPeriods = computed(() => this.tradingDay()?.periods ?? []);
	readonly symbolName = computed(() => this.symbol()?.name?.trim() ?? '');

	readonly marketState = computed(() => this.todayMarketSchedule().state);
	readonly stateStyle = computed(() => STATE_STYLE[this.marketState()]);
	readonly countdown = computed(() => nextMarketChange(this.selectedGroup(), this.currentTime()));
	readonly countdownText = computed(() => {
		const countdown = this.countdown();
		return countdown ? formatMarketCountdown(countdown, this.currentTime()) : '';
	});

	readonly headline = computed(() => {
		const symbolName = this.symbolName();
		if (symbolName) {
			return this.marketState() === 'closed'
				? `خارج از زمان مجاز ثبت سفارش ${symbolName}`
				: `زمان مجاز ثبت سفارش ${symbolName}`;
		}
		const subject = `بازار ${this.selectedTitle()}`;
		switch (this.marketState()) {
			case 'trading':
				return `${subject} باز است.`;
			case 'preTrading':
				return `پیش گشایش ${subject}`;
			default:
				return `${subject} بسته است!`;
		}
	});

	open(): Promise<void> {
		return this.sheet()?.open() ?? Promise.resolve();
	}
	close(): Promise<void> {
		return this.sheet()?.close() ?? Promise.resolve();
	}
}
